import { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import {
  ArrowLeft, Copy, Check, Truck, RotateCcw, ShoppingBag,
  AlertCircle,
} from 'lucide-react';
import type { Order } from '@/domain/entities/order.entity';
import { getOrderService } from '@/infrastructure/services/order.service';
import { OrderStatusBadge } from './OrderStatusBadge';
import { OrderProgressStepper } from './OrderProgressStepper';
import { OrderLineItem } from './OrderLineItem';
import { OrderSubtotal } from './OrderSubtotal';
import { ModifyOrderAccordion } from './ModifyOrderAccordion';
import { storefrontLayoutLogger } from '@/lib/logger';
import {
  formatOrderDate,
  formatFullAddress,
  isOrderActive,
  isOrderShipped,
  isOrderDelivered,
  canModifyOrder,
} from './utils';

const POLL_ACTIVE_MS = 60_000;
const POLL_STALE_MS = 300_000;
const RECENT_THRESHOLD_MS = 24 * 60 * 60 * 1000;

// --- Styled components ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xl};
`;

const BackRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: inherit;
  padding: ${({ theme }) => theme.space.xs};

  &:hover {
    text-decoration: underline;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MetaSeparator = styled.span`
  color: ${({ theme }) => theme.colors.border.color};
`;

const OrderNumberText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const CopyBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: inline-flex;
  align-items: center;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SummaryCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${({ theme }) => theme.space.xl};
  padding: ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.radius.regular};
  border: 1px solid ${({ theme }) => theme.colors.border.color};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryHeading = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.space.sm} 0;
`;

const SummaryText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const SummaryTextBold = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ItemsCard = styled.div`
  padding: ${({ theme }) => theme.space.xl};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  background: rgba(200, 170, 50, 0.1);
  border: 1px solid #d4b040;
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: #a08520;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const ActionLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.md};
`;

const ActionLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Loader = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// --- Component ---

interface OrderDetailProps {
  readonly orderId: string;
  readonly initialOrder?: Order;
  readonly locale?: string;
  readonly currencyCode?: string;
  readonly currencySymbol?: string;
  readonly onBack: () => void;
  readonly onNavigateToReturns?: () => void;
}

export function OrderDetail({
  orderId,
  initialOrder,
  locale = 'en-US',
  currencyCode = 'USD',
  currencySymbol,
  onBack,
  onNavigateToReturns,
}: OrderDetailProps) {
  const [order, setOrder] = useState<Order | null>(initialOrder ?? null);
  const [loading, setLoading] = useState(!initialOrder);
  const [copied, setCopied] = useState(false);

  const fetchOrder = useCallback(async () => {
    try {
      const svc = getOrderService();
      const data = await svc.getOrderById(orderId);
      setOrder(data);
    } catch (err) {
      storefrontLayoutLogger.error('Failed to fetch order detail', err instanceof Error ? err : undefined, {
        orderId,
      });
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!initialOrder) fetchOrder();
  }, [fetchOrder, initialOrder]);

  useEffect(() => {
    if (!order || !isOrderActive(order)) return;

    const lastUpdate = new Date(order.last_update_date).getTime();
    const isRecent = Date.now() - lastUpdate < RECENT_THRESHOLD_MS;
    const interval = isRecent ? POLL_ACTIVE_MS : POLL_STALE_MS;

    const timer = setInterval(fetchOrder, interval);
    return () => clearInterval(timer);
  }, [order, fetchOrder]);

  const handleCopy = () => {
    if (!order) return;
    navigator.clipboard.writeText(order.number);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <Loader>Loading order details...</Loader>;
  if (!order) return <Loader>Order not found.</Loader>;

  const active = isOrderActive(order);
  const shipped = isOrderShipped(order);
  const delivered = isOrderDelivered(order);
  const modifiable = canModifyOrder(order);

  const { lines: addressLines } = formatFullAddress(order.shipping_address);
  const paymentGateway = order.payments?.[0]?.gateway
    ? `${order.payments[0].gateway.charAt(0).toUpperCase()}${order.payments[0].gateway.slice(1)}`
    : null;

  return (
    <Container>
      {/* Back link */}
      <BackRow>
        <BackButton onClick={onBack}>
          <ArrowLeft size={18} />
          Back to Purchases
        </BackButton>
      </BackRow>

      {/* Title */}
      <Title>Order Details</Title>

      {/* Meta row: date | order # | status badge */}
      <MetaRow>
        <span>Order placed {formatOrderDate(order.purchase_date)}</span>
        <MetaSeparator>|</MetaSeparator>
        <OrderNumberText>
          Order # {order.number}
          <CopyBtn onClick={handleCopy} aria-label="Copy order number">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </CopyBtn>
        </OrderNumberText>
        <MetaSeparator>|</MetaSeparator>
        <OrderStatusBadge order={order} />
      </MetaRow>

      {/* Progress stepper for active orders */}
      {active && <OrderProgressStepper order={order} />}

      {active && !modifiable && (
        <InfoBanner>
          <AlertCircle size={20} />
          Your order is being processed and cannot be modified at this time.
        </InfoBanner>
      )}

      {/* Summary strip: Ship to | Payment method | Order Summary */}
      <SummaryCard>
        <SummaryCol>
          <SummaryHeading>Ship to</SummaryHeading>
          <SummaryTextBold>
            {order.shipping_address.first_name} {order.shipping_address.last_name}
          </SummaryTextBold>
          {addressLines.map((line) => (
            <SummaryText key={line}>{line}</SummaryText>
          ))}
        </SummaryCol>

        <SummaryCol>
          <SummaryHeading>Payment method</SummaryHeading>
          {paymentGateway ? (
            <SummaryTextBold>{paymentGateway}</SummaryTextBold>
          ) : (
            <SummaryText>N/A</SummaryText>
          )}
          {order.shipping_method && (
            <>
              <SummaryHeading style={{ marginTop: 16 }}>Shipping method</SummaryHeading>
              <SummaryText>{order.shipping_method.title || 'Standard Shipping'}</SummaryText>
            </>
          )}
        </SummaryCol>

        <SummaryCol>
          <SummaryHeading>Order Summary</SummaryHeading>
          <OrderSubtotal
            order={order}
            locale={locale}
            currencyCode={currencyCode}
            currencySymbol={currencySymbol}
          />
        </SummaryCol>
      </SummaryCard>

      {/* Line items */}
      <ItemsCard>
        {order.line_items?.map((item) => (
          <OrderLineItem
            key={item.id}
            item={item}
            locale={locale}
            currencyCode={currencyCode}
            currencySymbol={currencySymbol}
          />
        ))}
      </ItemsCard>

      {/* Modify order accordion (for active orders) */}
      {active && (
        <ModifyOrderAccordion
          order={order}
          onNavigateToReturns={onNavigateToReturns}
        />
      )}

      {/* Action links */}
      <ActionLinks>
        {shipped && (
          <ActionLink>
            <Truck size={16} />
            Track My Order
          </ActionLink>
        )}
        {delivered && (
          <ActionLink onClick={onNavigateToReturns}>
            <RotateCcw size={16} />
            Request a Return
          </ActionLink>
        )}
        {order.line_items?.[0] && (
          <ActionLink>
            <ShoppingBag size={16} />
            Buy Again
          </ActionLink>
        )}
      </ActionLinks>
    </Container>
  );
}
